// src/pages/admin/ManageElectionsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import ElectionProtectedRoute from '../../../components/auth/ElectionProtectedRoute';

const ManageElectionsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [schools, setSchools] = useState([]);
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    schoolId: '',
    title: '',
    description: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    checkAuth();
    fetchSchools();
  }, []);

  useEffect(() => {
    if (schools.length > 0) {
      fetchElections();
    }
  }, [schools]);

  const checkAuth = () => {
    const userData = localStorage.getItem('election_user');
    if (!userData) {
      navigate('/admin/election-login');
      return;
    }
    setUser(JSON.parse(userData));
  };

  const fetchSchools = async () => {
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/auth/assigned-schools`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSchools(data.data);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const fetchElections = async () => {
    try {
      const token = localStorage.getItem('election_token');
      
      // Fetch elections for each school
      const allElections = [];
      for (const school of schools) {
        const response = await fetch(`${API_BASE_URL_ELECTIONS}/elections/school/${school.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          allElections.push(...data.data.map(e => ({ ...e, school })));
        }
      }
      
      setElections(allElections);
    } catch (error) {
      console.error('Error fetching elections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/elections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setShowModal(false);
        setFormData({
          schoolId: '',
          title: '',
          description: '',
          startDate: '',
          endDate: ''
        });
        fetchElections();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error creating election:', error);
      alert('Failed to create election');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (electionId) => {
    if (!ConfirmDialog('Are you sure you want to delete this election?')) return;

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/elections/${electionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchElections();
      }
    } catch (error) {
      console.error('Error deleting election:', error);
      alert('Failed to delete election');
    }
  };

  const isElectionActive = (election) => {
    const now = new Date();
    const start = new Date(election.startDate);
    const end = new Date(election.endDate);
    return now >= start && now <= end && election.isActive;
  };

  const getElectionStatus = (election) => {
    const now = new Date();
    const start = new Date(election.startDate);
    const end = new Date(election.endDate);

    if (!election.isActive) return { text: 'Inactive', color: 'gray' };
    if (now < start) return { text: 'Upcoming', color: 'blue' };
    if (now >= start && now <= end) return { text: 'Active', color: 'green' };
    if (now > end) return { text: 'Ended', color: 'red' };
    return { text: 'Unknown', color: 'gray' };
  };

  if (loading && elections.length === 0) {
    return (
      <ElectionProtectedRoute>
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-400 font-semibold">Loading Elections...</p>
          </div>
        </div>
      </ElectionProtectedRoute>
    );
  }

  return (
    <ElectionProtectedRoute>
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border-b border-red-900/30 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <button
                  onClick={() => navigate('/admin/election-dashboard')}
                  className="flex items-center gap-2 text-red-400 hover:text-red-300 mb-3 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Dashboard
                </button>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">🗳️</span>
                  Manage Elections
                </h1>
                <p className="text-gray-400 mt-1">Create and manage school elections</p>
              </div>
              <button
                onClick={() => setShowModal(true)}
                disabled={schools.length === 0}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Election
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {schools.length === 0 ? (
            <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-16">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-red-900/20 rounded-full mb-6 border border-red-500/30">
                  <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">No schools assigned</h3>
                <p className="text-gray-400">Contact super admin to assign schools to your account</p>
              </div>
            </div>
          ) : elections.length === 0 ? (
            <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-16">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-red-900/20 rounded-full mb-6 border border-red-500/30">
                  <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">No elections yet</h3>
                <p className="text-gray-400 mb-6">Create your first election to get started</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create First Election
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {elections.map((election) => {
                const status = getElectionStatus(election);
                return (
                  <div key={election.id} className="group relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
                    <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gray-800 group-hover:border-red-500/50 rounded-xl shadow-xl p-6 transition-all duration-300">
                      {/* School Badge */}
                      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-800">
                        {election.school.logo ? (
                          <img src={election.school.logo} alt={election.school.name} className="w-8 h-8 rounded object-cover border border-red-500/30" />
                        ) : (
                          <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-red-700 rounded flex items-center justify-center text-white font-bold text-sm border border-red-500/30">
                            {election.school.shortName || election.school.name.charAt(0)}
                          </div>
                        )}
                        <span className="text-sm text-gray-400">{election.school.shortName || election.school.name}</span>
                      </div>

                      {/* Election Info */}
                      <h3 className="text-xl font-bold text-white mb-2">{election.title}</h3>
                      <p className="text-sm text-gray-400 mb-4 line-clamp-2">{election.description}</p>

                      {/* Status Badge */}
                      <div className="mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                          status.color === 'green' ? 'bg-green-900/30 text-green-400 border-green-500/50' :
                          status.color === 'blue' ? 'bg-blue-900/30 text-blue-400 border-blue-500/50' :
                          status.color === 'red' ? 'bg-red-900/30 text-red-400 border-red-500/50' :
                          'bg-gray-800 text-gray-400 border-gray-600'
                        }`}>
                          {status.text}
                        </span>
                      </div>

                      {/* Dates */}
                      <div className="space-y-2 mb-4 text-sm bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center gap-2 text-gray-300">
                          <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Start: {new Date(election.startDate).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>End: {new Date(election.endDate).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Link 
                          to={`/admin/elections/${election.id}/manage`}
                          className="flex-1 text-center px-3 py-2 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg text-sm font-semibold transition-all duration-300 border border-gray-600 hover:border-red-500/50"
                        >
                          Manage
                        </Link>
                        <Link 
                          to={`/admin/elections/${election.id}/results`}
                          className="flex-1 text-center px-3 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg text-sm font-semibold transition-all duration-300 shadow-lg shadow-green-600/20"
                        >
                          Results
                        </Link>
                        <button
                          onClick={() => handleDelete(election.id)}
                          className="px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg text-sm font-semibold transition-all duration-300 shadow-lg shadow-red-600/20"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Election Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl max-w-2xl w-full p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            {/* Decorative border glow */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 blur"></div>
            
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">🗳️</span>
                  Create New Election
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    School <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.schoolId}
                    onChange={(e) => setFormData({ ...formData, schoolId: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select School</option>
                    {schools.map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Election Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="SRC Elections 2025"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="Student Representative Council Elections"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-800">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 text-white rounded-lg font-semibold transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </span>
                    ) : (
                      'Create Election'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </ElectionProtectedRoute>
  );
};

export default ManageElectionsPage;